import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getReturnLabel, getReturnPath } from "../../../utils/navReturn";
import useAuth from "../../../hooks/useAuth";
import { canReviewConfirmations } from "../../../utils/permissions";
import confirmationService from "../../../services/confirmation.service";
import ConfirmationExcelList from "./ConfirmationExcelList";
import {
  CONFIRMATION_VIEWS,
  getListEmptyMessage,
  getListSectionTitle,
  sortConfirmationsNewestFirst,
} from "./confirmationListUtils";
import { formatPendingListCount, vehicleRequestsPath } from "./groupPendingConfirmations";
import { onDashboardRefresh } from "../../../utils/dashboardEvents";
import PaginationBar from "../../../components/common/PaginationBar";
import ExportExcelButton from "../../../components/common/ExportExcelButton";
import { saveExcelBlob, getApiErrorMessage } from "../../../utils/downloadExcel";
import { PAGE_LIMIT } from "../../../constants/pagination";
import { readListItems, readPagination } from "../../../utils/listPagination";
import "../../../styles/users.css";
import "../../../styles/confirmation.css";

function resolveListFilter(statusParam, isRepoAdmin, listMode) {
  if (listMode === "seized") return "CONFIRMED";
  const status = String(statusParam || "").toUpperCase();
  if (status === "PENDING" || status === "CONFIRMED" || status === "REJECTED") {
    return status;
  }
  return isRepoAdmin ? "PENDING" : "CONFIRMED";
}

function resolveView(viewParam, listMode) {
  if (listMode === "seized") return CONFIRMATION_VIEWS.INVENTORY_CONFIRMED;
  const view = String(viewParam || "").toLowerCase();
  if (view === CONFIRMATION_VIEWS.INVENTORY_CONFIRMED) {
    return CONFIRMATION_VIEWS.INVENTORY_CONFIRMED;
  }
  if (view === CONFIRMATION_VIEWS.INVENTORY_PENDING_APPROVAL) {
    return CONFIRMATION_VIEWS.INVENTORY_PENDING_APPROVAL;
  }
  return "";
}

export default function Confirmation({ listMode = "" }) {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isRepoAdmin = canReviewConfirmations(auth);
  const isFieldUser = !isRepoAdmin;
  const returnTo = getReturnPath(searchParams, location.state);
  const returnLabel = getReturnLabel(returnTo);

  const statusParam = searchParams.get("status") || "";
  const viewParam = searchParams.get("view") || "";
  const listFilter = useMemo(
    () => resolveListFilter(statusParam, isRepoAdmin, listMode),
    [statusParam, isRepoAdmin, listMode]
  );
  const listView = useMemo(() => resolveView(viewParam, listMode), [viewParam, listMode]);

  const [confirmations, setConfirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [vehicleGroups, setVehicleGroups] = useState([]);
  const [vehicleTotal, setVehicleTotal] = useState(0);
  const [requestTotal, setRequestTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  const loadSeqRef = useRef(0);
  const groupPendingByVehicle = listFilter === "PENDING" && !listView;
  const uniqueConfirmedByVehicle =
    listFilter === "CONFIRMED" &&
    (!listView || listView === CONFIRMATION_VIEWS.INVENTORY_CONFIRMED);

  useEffect(() => {
    setPage(1);
  }, [listFilter, listView, appliedQuery]);

  useEffect(() => {
    if (listMode === "seized") return;
    const normalized = String(statusParam || "").toUpperCase();
    if (normalized === "PENDING" || normalized === "CONFIRMED" || normalized === "REJECTED") {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("status", isRepoAdmin ? "PENDING" : "CONFIRMED");
    const from = searchParams.get("from");
    if (from) next.set("from", from);
    setSearchParams(next, { replace: true });
  }, [statusParam, isRepoAdmin, searchParams, setSearchParams, listMode]);

  const loadConfirmations = useCallback(async () => {
    if (!auth?.token) {
      setLoading(false);
      return;
    }

    const seq = ++loadSeqRef.current;

    try {
      setLoading(true);
      setError("");

      const params = { status: listFilter, page, limit: PAGE_LIMIT };
      if (listView) params.view = listView;
      if (appliedQuery.trim()) params.q = appliedQuery.trim();
      if (groupPendingByVehicle) params.groupBy = "vehicle";
      if (uniqueConfirmedByVehicle) params.uniqueBy = "vehicle";
      const res = await confirmationService.getAll(auth.token, params);
      if (seq !== loadSeqRef.current) return;

      const items = readListItems(res);
      setConfirmations(items);
      setPagination(readPagination(res));
      setVehicleGroups(Array.isArray(res?.groups) ? res.groups : []);
      setVehicleTotal(Number(res?.vehicleTotal || 0) || 0);
      setRequestTotal(Number(res?.requestTotal || 0) || 0);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      setError(err?.response?.data?.message || "Failed to load confirmations");
      setConfirmations([]);
      setVehicleGroups([]);
      setVehicleTotal(0);
      setRequestTotal(0);
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false);
      }
    }
  }, [auth?.token, listFilter, listView, page, appliedQuery, groupPendingByVehicle, uniqueConfirmedByVehicle]);

  useEffect(() => {
    return onDashboardRefresh(loadConfirmations);
  }, [loadConfirmations]);

  useEffect(() => {
    loadConfirmations();
  }, [loadConfirmations]);

  useEffect(() => {
    setSearchQuery("");
    setAppliedQuery("");
  }, [listFilter, listView]);

  const displayedConfirmations = useMemo(
    () => sortConfirmationsNewestFirst(confirmations, listFilter, listView),
    [confirmations, listFilter, listView]
  );

  const openConfirmation = (item) => {
    if (!isRepoAdmin || !item?._id) return;
    const listReturn = `${location.pathname}${location.search}`;
    navigate(`/confirmation/${item._id}`, { state: { from: listReturn } });
  };

  const openVehicleGroup = (group) => {
    if (!group?.vehicleKey) return;
    const listReturn = `${location.pathname}${location.search}`;
    navigate(vehicleRequestsPath(group.vehicleKey), {
      state: {
        from: listReturn,
        vehicleNumber: group.vehicleNumber || "",
      },
    });
  };

  const openInventory = (item) => {
    if (!item?._id) return;
    navigate(`/inventory-update?confirmationId=${item._id}`);
  };

  const onDownloadExcel = async () => {
    if (!auth?.token) return;
    setExporting(true);
    setError("");
    try {
      const params = { status: listFilter };
      if (listView) params.view = listView;
      if (appliedQuery.trim()) params.q = appliedQuery.trim();
      if (uniqueConfirmedByVehicle) params.uniqueBy = "vehicle";
      const response = await confirmationService.downloadExcel(
        auth.token,
        params
      );
      const fallbackName =
        listView === CONFIRMATION_VIEWS.INVENTORY_CONFIRMED
          ? "seized-successfully.xlsx"
          : listFilter === "PENDING"
            ? "pending-confirmations.xlsx"
            : listFilter === "REJECTED"
              ? "cancel-confirmations.xlsx"
              : "confirmed-cases.xlsx";
      await saveExcelBlob(response, fallbackName);
    } catch (err) {
      setError(await getApiErrorMessage(err, "Failed to download Excel"));
    } finally {
      setExporting(false);
    }
  };

  const pageTitle = getListSectionTitle(listFilter, listView);
  const baseEmptyMessage = getListEmptyMessage(listFilter, listView, isRepoAdmin);
  const emptyMessage = appliedQuery.trim()
    ? `No cases match “${appliedQuery.trim()}”.`
    : baseEmptyMessage;

  return (
    <div className={`page cf-page cf-page--list-only${isFieldUser ? " cf-page--user" : ""}`}>
      {listMode !== "seized" ? (
        <div className="cf-view-top">
          <Link to={returnTo} className="cf-view-back">
            ← Back to {returnLabel}
          </Link>
        </div>
      ) : null}

      <header className="cf-page__title-row">
        <h2 className="cf-page__title">{pageTitle}</h2>
        {!loading && confirmations.length > 0 ? (
          <p className="cf-list-count" aria-live="polite">
            {groupPendingByVehicle && (vehicleTotal > 0 || requestTotal > 0)
              ? formatPendingListCount(
                  vehicleTotal || vehicleGroups.length,
                  requestTotal || confirmations.length
                )
              : displayedConfirmations.length === confirmations.length
                ? `${confirmations.length} case${confirmations.length === 1 ? "" : "s"}`
                : `${displayedConfirmations.length} of ${confirmations.length}`}
          </p>
        ) : null}
      </header>

      {error ? <p className="error-text">{error}</p> : null}

      <section className="cf-table-card card cf-table-card--responsive">
        <form
          className="cf-list-search"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setAppliedQuery(searchQuery.trim());
          }}
        >
          <label className="cf-list-search__field">
            <span className="sr-only">Search confirmations</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                isRepoAdmin
                  ? "Search vehicle, customer, bank, chassis, loan…"
                  : "Search vehicle, customer, chassis, loan…"
              }
              autoComplete="off"
            />
          </label>
          <button type="submit" className="primary-page-btn cf-list-search__btn">
            Search
          </button>
          <button
            type="button"
            className="secondary-page-btn cf-list-search__btn"
            onClick={() => {
              setSearchQuery("");
              setAppliedQuery("");
              setPage(1);
            }}
          >
            Reset
          </button>
          <ExportExcelButton onClick={onDownloadExcel} loading={exporting} />
        </form>

        {loading ? (
          <p className="cf-excel-loading">Loading…</p>
        ) : displayedConfirmations.length === 0 ? (
          <p className="cf-excel-loading cf-list-empty">{emptyMessage}</p>
        ) : (
          <ConfirmationExcelList
            items={displayedConfirmations}
            groups={groupPendingByVehicle ? vehicleGroups : null}
            groupByVehicle={groupPendingByVehicle}
            isRepoAdmin={isRepoAdmin}
            listFilter={listFilter}
            listView={listView}
            startSerial={Math.max(
              Number(pagination.total || displayedConfirmations.length) -
                ((pagination.page || page) - 1) * PAGE_LIMIT,
              displayedConfirmations.length
            )}
            onView={openConfirmation}
            onInventory={openInventory}
            onOpenGroup={openVehicleGroup}
          />
        )}
      </section>
      <PaginationBar
        page={pagination.page || page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        loading={loading}
        onPageChange={setPage}
      />
    </div>
  );
}
